// src/pages/sessions/components/LiveSession/Controls/MediaControls.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  MicrophoneIcon,
  VideoCameraIcon,
  ComputerDesktopIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import {
  MicrophoneIcon as MicrophoneSolidIcon,
  VideoCameraIcon as VideoCameraSolidIcon,
  ComputerDesktopIcon as ComputerDesktopSolidIcon,
} from '@heroicons/react/24/solid';

/**
 * A grayed-out screen share button that shows a tooltip on tap/click.
 * Implemented as a real <button> so iOS Safari reliably fires touch events.
 * The tooltip is rendered in a portal-like div positioned above the button
 * and auto-dismisses after 2.5 seconds or on outside tap.
 */
const ScreenShareUnsupportedButton = ({ t }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hideTimerRef = useRef(null);
  const btnRef = useRef(null);

  const showTooltip = () => {
    setTooltipVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setTooltipVisible(false), 2500);
  };

  useEffect(() => {
    if (!tooltipVisible) return;
    const hide = () => setTooltipVisible(false);
    document.addEventListener('pointerdown', hide, { once: true });
    return () => document.removeEventListener('pointerdown', hide);
  }, [tooltipVisible]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const message = t('controls.screenShareNotSupported', 'Screen sharing is not supported on this device');

  return (
    <div className="relative flex items-center justify-center">
      {/* Tooltip — renders above the button */}
      {tooltipVisible && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50
                     bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl
                     whitespace-nowrap border border-gray-700 pointer-events-none"
          style={{ maxWidth: 220, whiteSpace: 'normal', textAlign: 'center' }}
        >
          {message}
          {/* Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2
                          border-4 border-transparent border-t-gray-900" />
        </div>
      )}

      {/* Real <button> so iOS fires click reliably */}
      <button
        ref={btnRef}
        type="button"
        onClick={showTooltip}
        className="p-3 rounded-full bg-gray-700 opacity-50 text-gray-400"
        aria-label={message}
        aria-disabled="true"
      >
        <ComputerDesktopIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

const MediaControls = ({
  audioEnabled,
  videoEnabled,
  screenSharing,
  embeddedDocument,
  videoToggling,
  canToggleCamera,
  canToggleMicrophone,
  cameraLocked = false,
  microphoneLocked = false,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  userMutedAudio,
  userMutedVideo,
  hasUserInteracted,
  canScreenShare = true, // false on tablet — initiation not supported
}) => {
  const { t } = useTranslation();

  // Lock should only show when media is ON and locked
  const showCameraLock = cameraLocked && videoEnabled;
  const showMicLock = microphoneLocked && audioEnabled;
  
  // CRITICAL FIX: Disable buttons when locked ON
  const canActuallyToggleMic = canToggleMicrophone && !(microphoneLocked && audioEnabled);
  const canActuallyToggleCam = canToggleCamera && !(cameraLocked && videoEnabled);
  
  const isShareActive = !!screenSharing || !!embeddedDocument;
  
  // Get microphone tooltip
  const getMicrophoneTooltip = () => {
    if (showMicLock) return t('controls.microphoneLockedByTeacher');
    if (!canActuallyToggleMic) return t('controls.microphoneDisabled');
    return audioEnabled ? t('controls.muteMicrophone') : t('controls.unmuteMicrophone');
  };

  // Get camera tooltip
  const getCameraTooltip = () => {
    if (showCameraLock) return t('controls.cameraLockedByTeacher');
    if (!canActuallyToggleCam) return t('controls.cameraDisabled');
    return videoEnabled ? t('controls.stopCamera') : t('controls.startCamera');
  };
  
  return (
    <>
      {/* Microphone Button */}
      <div className="relative">
        <button
          onClick={onToggleAudio}
          disabled={!canActuallyToggleMic}
          className={`p-3 rounded-full transition-colors ${
            userMutedAudio && hasUserInteracted
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          } ${!canActuallyToggleMic ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={getMicrophoneTooltip()}
        >
          {audioEnabled ? (
            <MicrophoneIcon className="w-5 h-5" />
          ) : (
            <MicrophoneSolidIcon className="w-5 h-5" />
          )}
        </button>
        {showMicLock && (
          <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-0.5">
            <LockClosedIcon className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      {/* Camera Button */}
      <div className="relative">
        <button
          onClick={onToggleVideo}
          disabled={!canActuallyToggleCam || videoToggling}
          className={`p-3 rounded-full transition-colors ${
            userMutedVideo && hasUserInteracted
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          } ${(!canActuallyToggleCam || videoToggling) ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={getCameraTooltip()}
        >
          {videoToggling ? (
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : videoEnabled ? (
            <VideoCameraIcon className="w-5 h-5" />
          ) : (
            <VideoCameraSolidIcon className="w-5 h-5" />
          )}
        </button>
        {showCameraLock && (
          <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-0.5">
            <LockClosedIcon className="w-3 h-3 text-white" />
          </div>
        )}
      </div>

      {/* Screen Share Button — grayed out on devices that don't support getDisplayMedia.
          Uses a visible tooltip on tap since iOS ignores most synthetic events on
          non-interactive-looking elements. */}
      {!canScreenShare ? (
        <ScreenShareUnsupportedButton t={t} />
      ) : (
        <button
          type="button"
          onClick={onToggleScreenShare}
          className={`p-3 rounded-full transition-colors ${
            isShareActive
              ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          }`}
          title={isShareActive ? t('controls.stopSharing') : t('controls.shareScreen')}
        >
          {isShareActive ? (
            <ComputerDesktopSolidIcon className="w-5 h-5" />
          ) : (
            <ComputerDesktopIcon className="w-5 h-5" />
          )}
        </button>
      )}
    </>
  );
};

export default MediaControls;